import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { YEARS } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const DuplicateYearDialog = ({ open, onClose, companyId, sourceYear, onDone }) => {
  const [target, setTarget] = useState(sourceYear + 1);
  const [overwrite, setOverwrite] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setTarget(YEARS.includes(sourceYear + 1) ? sourceYear + 1 : YEARS[YEARS.length - 1]); setOverwrite(false); } }, [open, sourceYear]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/employees/duplicate-year`, { source: sourceYear, target, overwrite });
      toast.success(`${data.updated} salarié(s) dupliqué(s) vers ${target}${data.skipped ? ` · ${data.skipped} ignoré(s)` : ""}`);
      onDone(target);
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="duplicate-year-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Dupliquer l'exercice {sourceYear}</DialogTitle>
          <DialogDescription>Copie les rémunérations mensuelles de tous les salariés de {sourceYear} vers l'exercice cible. Les salariés sans rémunération en {sourceYear} sont ignorés.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Exercice cible</Label>
            <Select value={String(target)} onValueChange={(v) => setTarget(Number(v))}>
              <SelectTrigger className="font-mono" data-testid="duplicate-target-select"><SelectValue /></SelectTrigger>
              <SelectContent>{YEARS.filter((y) => y !== sourceYear).map((y) => <SelectItem key={y} value={String(y)} data-testid={`duplicate-target-${y}`}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3 rounded-md border border-border p-3 bg-muted/50">
            <Switch checked={overwrite} onCheckedChange={setOverwrite} data-testid="duplicate-overwrite-switch" />
            <div className="text-sm">
              <div className="font-medium text-foreground">Écraser les montants déjà saisis en {target}</div>
              <div className="text-xs text-muted-foreground">Sinon, les salariés ayant déjà des rémunérations en {target} sont conservés tels quels.</div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={busy} data-testid="duplicate-submit">{busy ? "Duplication…" : `Dupliquer vers ${target}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
